import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { adminService } from "services/adminService";
import { academicService } from "services/academicService";
import { r2Service } from "services/r2Service";
import { normalizeSelectOptions, type SelectOption } from "lib/selectOptions";
import { toastError } from "lib/toast";
import {
  buildInstructorPayload,
  instructorFormFromApi,
  validateInstructorForm,
} from "./helpers";
import { emptyInstructorForm, type InstructorFormOptions, type InstructorFormValues } from "./types";

type UseInstructorFormArgs = {
  open: boolean;
  editingId: number | null;
  isArabic: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

export function useInstructorForm({ open, editingId, isArabic, onClose, onSaved }: UseInstructorFormArgs) {
  const [form, setForm] = useState<InstructorFormValues>(emptyInstructorForm);
  const [options, setOptions] = useState<InstructorFormOptions>({
    stages: [],
    grades: [],
    subjects: [],
  });
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const updateForm = useCallback(
    (updater: (previous: InstructorFormValues) => InstructorFormValues) => setForm(updater),
    []
  );

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const [stagesRes, gradesRes, subjectsRes] =
        await Promise.all([
          academicService.listStages({ limit: 500, sort_by: "name", sort_direction: "asc" }),
          adminService.getGrades({ limit: 500, sort_by: "name", sort_direction: "asc", "filter[is_active]": true }),
          adminService.getSubjects({ limit: 500, sort_by: "name", sort_direction: "asc" }),
        ]);

      setOptions({
        stages: ((stagesRes?.data || []) as InstructorFormOptions["stages"]).filter((stage) => stage.slug !== "university"),
        grades: (gradesRes?.data || []) as InstructorFormOptions["grades"],
        subjects: (subjectsRes?.data || []) as InstructorFormOptions["subjects"],
      });
    } catch (error) {
      console.error("Failed to load instructor form options:", error);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void loadOptions();
  }, [open, loadOptions]);

  useEffect(() => {
    if (!open) {
      setForm(emptyInstructorForm());
      return;
    }

    if (!editingId) {
      setForm(emptyInstructorForm());
      return;
    }

    let cancelled = false;
    setLoadingDetail(true);
    void adminService
      .getInstructor(editingId)
      .then((row) => {
        if (!cancelled) setForm(instructorFormFromApi(row as Record<string, any>));
      })
      .catch((error) => console.error("Failed to load instructor:", error))
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, editingId]);

  const stageOptions = useMemo<SelectOption[]>(() => normalizeSelectOptions(options.stages), [options.stages]);
  const gradeOptions = useMemo<SelectOption[]>(() => {
    const rows = form.stageIds.length
      ? options.grades.filter((grade) => form.stageIds.includes(grade.stage_id))
      : options.grades;
    return normalizeSelectOptions(rows);
  }, [form.stageIds, options.grades]);
  const subjectOptions = useMemo<SelectOption[]>(() => {
    const rows = form.stageIds.length
      ? options.subjects.filter((subject) => (subject.stage_ids?.length ? subject.stage_ids : [subject.stage_id]).some((id) => id != null && form.stageIds.includes(id)))
      : options.subjects;
    return normalizeSelectOptions(rows, (subject) => subject.name);
  }, [form.stageIds, options.subjects]);

  const handleImageFileChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const result = await r2Service.uploadWithPresign(file, "avatars");
      updateForm((previous) => ({ ...previous, image: result.key, imagePreview: result.url }));
    } catch (error) {
      console.error("Failed to upload instructor image:", error);
      toastError(isArabic ? "تعذر رفع صورة المدرّس." : "Failed to upload instructor image.");
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  }, [isArabic, updateForm]);

  const handleSubmit = useCallback(async () => {
    const validationMessage = validateInstructorForm(form, isArabic);
    if (validationMessage) {
      toastError(validationMessage);
      return;
    }

    setSaving(true);
    try {
      const payload = buildInstructorPayload(form);
      if (editingId) await adminService.updateInstructor(editingId, payload);
      else await adminService.createInstructor(payload);
      onClose();
      await onSaved();
    } catch (error) {
      console.error("Failed to save instructor:", error);
      toastError(isArabic ? "تعذر حفظ بيانات المدرّس." : "Failed to save instructor.");
    } finally {
      setSaving(false);
    }
  }, [editingId, form, isArabic, onClose, onSaved]);

  return {
    form,
    updateForm,
    loadingDetail,
    saving,
    uploadingImage,
    optionsLoading,
    stageOptions,
    gradeOptions,
    subjectOptions,
    rawOptions: options,
    handleImageFileChange,
    handleSubmit,
  };
}

export type InstructorFormController = ReturnType<typeof useInstructorForm>;
